import React from 'react';
import { Link } from 'react-router-dom';
import { 
  FileText, 
  Download, 
  Mail, 
  Calendar,
  ArrowRight,
  Sparkles,
  Newspaper,
  Mic,
  Video
} from 'lucide-react';

const Press = () => {
  const pressReleases = [
    {
      date: 'January 15, 2026',
      title: 'Platform Reaches 10,000 Active Users Milestone',
      category: 'Company News',
      description: 'We\'re excited to announce that our platform has reached 10,000 active users, marking a significant milestone in our mission to connect clients with exceptional vendors.',
      icon: Newspaper
    },
    {
      date: 'December 20, 2025',
      title: 'New Features Launch: Enhanced Booking Experience',
      category: 'Product Update',
      description: 'We\'ve launched new features including real-time availability, instant messaging, and improved search filters to make event planning even easier.',
      icon: FileText
    },
    {
      date: 'November 10, 2025',
      title: 'Partnership with Top Event Venues',
      category: 'Partnerships',
      description: 'We\'re thrilled to announce partnerships with leading event venues across the country, expanding our network of trusted partners.',
      icon: Sparkles
    },
    {
      date: 'October 5, 2025',
      title: 'Platform Expansion to New Markets',
      category: 'Company News',
      description: 'We\'re expanding our services to new cities and regions, bringing our platform to more clients and vendors nationwide.',
      icon: ArrowRight
    }
  ];

  const mediaKit = {
    logo: 'Brand logos and assets',
    images: 'High-resolution product screenshots',
    factSheet: 'Company information and statistics',
    teamPhotos: 'Executive team photos'
  };

  const contactInfo = {
    email: 'press@vendalist.com',
    phone: '+233 XX XXX XXXX',
    address: 'Accra, Ghana'
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-gray-50 to-white border-b border-gray-100">
        <div className="page-container py-12 md:py-20 px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-black text-white px-4 py-2 rounded-full text-sm font-light mb-6">
              <Sparkles className="w-4 h-4" />
              <span>Press & Media</span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Press Center
            </h1>
            
            <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed max-w-3xl mx-auto">
              Stay updated with our latest news, press releases, and company updates. 
              For media inquiries, please contact our press team.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#press-releases"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-black text-white rounded-xl font-light hover:bg-gray-800 transition-colors"
              >
                View Press Releases
                <ArrowRight className="w-4 h-4" />
              </a>
              <a
                href="#media-kit"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-light hover:border-gray-400 transition-colors"
              >
                Download Media Kit
                <Download className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Press Releases Section */}
      <div id="press-releases" className="py-12 md:py-20 bg-white">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Latest Press Releases
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Stay informed about our latest announcements and company news.
              </p>
            </div>

            <div className="space-y-6">
              {pressReleases.map((release, index) => {
                const Icon = release.icon;
                return (
                  <div 
                    key={index}
                    className="bg-white rounded-xl p-6 border border-gray-200 hover:shadow-lg transition-all duration-300"
                  >
                    <div className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-gray-900 text-white rounded-xl flex items-center justify-center">
                        <Icon className="w-6 h-6" />
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-3 mb-2">
                          <span className="text-sm text-gray-500 flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {release.date}
                          </span>
                          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded-full">
                            {release.category}
                          </span>
                        </div>
                        <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                          {release.title}
                        </h3>
                        <p className="text-gray-600 leading-relaxed">
                          {release.description}
                        </p>
                        <button className="mt-4 text-gray-900 font-medium hover:text-gray-700 flex items-center gap-2">
                          Read More
                          <ArrowRight className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Media Kit Section */}
      <div id="media-kit" className="py-12 md:py-20 bg-gray-50">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Media Kit
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Download our media kit for logos, images, and company information.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-6 md:p-8 border border-gray-200">
              <div className="grid sm:grid-cols-2 gap-6">
                {Object.entries(mediaKit).map(([key, value], index) => (
                  <div key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 bg-gray-900 text-white rounded-lg flex items-center justify-center">
                      <Download className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </h3>
                      <p className="text-sm text-gray-600">{value}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 pt-8 border-t border-gray-200">
                <button className="w-full sm:w-auto px-6 py-3 bg-black text-white rounded-xl font-light hover:bg-gray-800 transition-colors flex items-center justify-center gap-2">
                  <Download className="w-5 h-5" />
                  Download Full Media Kit
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contact Section */}
      <div className="py-12 md:py-20 bg-white">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12 md:mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Media Inquiries
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                For press inquiries, interview requests, or media partnerships, please contact us.
              </p>
            </div>

            <div className="bg-gray-50 rounded-2xl p-6 md:p-8 border border-gray-200">
              <div className="grid sm:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-900 text-white rounded-xl mb-3">
                    <Mail className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">Email</h3>
                  <a href={`mailto:${contactInfo.email}`} className="text-gray-600 hover:text-gray-900">
                    {contactInfo.email}
                  </a>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-900 text-white rounded-xl mb-3">
                    <Mic className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">Phone</h3>
                  <a href={`tel:${contactInfo.phone}`} className="text-gray-600 hover:text-gray-900">
                    {contactInfo.phone}
                  </a>
                </div>
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-900 text-white rounded-xl mb-3">
                    <FileText className="w-6 h-6" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-1">Location</h3>
                  <p className="text-gray-600">{contactInfo.address}</p>
                </div>
              </div>

              <div className="text-center">
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-black text-white rounded-xl font-light hover:bg-gray-800 transition-colors">
                  <Mail className="w-5 h-5" />
                  Contact Press Team
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-12 md:py-20 bg-gray-900 text-white">
        <div className="page-container px-4 sm:px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Stay Updated
            </h2>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              Subscribe to our newsletter to receive the latest news and updates directly in your inbox.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center max-w-md mx-auto">
              <input
                type="email"
                placeholder="Enter your email"
                className="flex-1 px-4 py-3 rounded-xl text-gray-900 border-0 focus:ring-2 focus:ring-white"
              />
              <button className="px-6 py-3 bg-white text-black rounded-xl font-light hover:bg-gray-100 transition-colors whitespace-nowrap">
                Subscribe
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Press;

